/**
 * Bootstrap do delivery V2 (Strangler Fig).
 *
 * Cada peça do V1 (delivery_app.html) migra como ilha React isolada.
 * V1 continua dono do estado global. Bundle reversível: se falhar,
 * V1 segue 100% funcional.
 *
 * Ilhas ativas:
 *   1. #delivery-v2-preview-root → ProductPreviewIsland
 *      (override de window.openProductPreview)
 *   2. #home-view                → HomeViewIsland (Header + Highlights +
 *      Pills + Cards inline com steppers). V1 `renderHome()` early-return
 *      via flag __V2_HOME_OWNED__.
 *   3. (REMOVIDA) CartBarIsland — barra "Ver Sacola" foi descontinuada
 *      pra não atrapalhar visualização; BottomNav Sacola ganhou pulse +
 *      valor total quando tem itens
 *   4. body (host invisível)     → BottomNavIsland (Home/Loja/Sacola/Conta)
 *   5. body (host invisível)     → Wizards (Simple/Pizza/Combo)
 *   6. body (host invisível)     → CartModalIsland (sacola — step 1 do
 *      checkout V1; finalizar delega pro V1 a partir do endereço/pagto)
 *   7. body (host invisível)     → UpsellModalIsland (offerings entre
 *      sacola e endereço; substitui `#upsell-modal` V1)
 *   8. body (host invisível)     → UserAreaIsland (Pedidos + Endereços +
 *      Dados em tabs; substitui `openUserArea(tab)` V1)
 *   9. body (host invisível)     → LoginIsland (wizard 5 steps:
 *      identifier/password/register/recover-channel/recover-confirm;
 *      substitui `toggleLoginModal()` V1)
 *  10. body (host invisível)     → CheckoutIsland (single-page com
 *      endereço + frete + pagamento; substitui `#checkout-modal` V1
 *      e o fluxo `nextCheckoutStep()` → `createUnifiedOrder()`).
 *  11. body (host invisível)     → PixModalIsland (QR + copy + polling
 *      WebSocket; substitui `#pix-modal` V1; ouve `aliv:payment-approved`
 *      que V1 socket.onmessage dispatcha quando MP confirma).
 *  12. body (host invisível)     → OrderTrackingIsland (modal "acompanhar
 *      pedido" pós-checkout; substitui `#order-details-modal` V1;
 *      timeline em tempo real via `aliv:order-status-changed` + polling
 *      15s fallback; aberto auto após Checkout.success e PIX.approved).
 *  13. body (host invisível)     → StoreInfoIsland (modal "Sobre a loja"
 *      com horários, endereço, contatos WhatsApp, mapa, redes sociais;
 *      substitui SweetAlert `openStoreInfo()` V1).
 */
import { StrictMode } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from '@/shared/ErrorBoundary';
// ProductPreviewIsland removido: clique no produto vai DIRETO pro wizard
// agora (sem passo intermediário de teaser).
import { track, funilAcesso } from './utils/tracking';
import { HomeViewIsland } from './components/HomeViewIsland';
import { MenuDrawerIsland } from './components/MenuDrawerIsland';
import { iniciarIdioma, t } from './i18n';
import {
  NetworkUnitPickerIsland, ehRedeComEscolha, esquecerUnidade, lembrarUnidade,
  unidadeLembrada, unidadePorSlug, type NetworkUnit,
} from './components/NetworkUnitPickerIsland';
import { BottomNavIsland } from './components/BottomNavIsland';
import { PwaInstallBannerIsland } from './components/PwaInstallBannerIsland';
import { PushNotificationsIsland } from './components/PushNotificationsIsland';
import { CookieAvisoIsland } from './components/CookieAvisoIsland';
import { GlobalSearchIsland } from './components/GlobalSearchIsland';
import { TableModeBannerIsland } from './components/TableModeBannerIsland';
import { FreeShippingBanner } from './components/FreeShippingBanner';
import { RaffleBanner } from './components/RaffleBanner';
// Wizards lazy-loaded via LazyOnEvent (5B — 17/05). Antes eram eager
// (~162 KB raw / ~40 KB gzip). Cliente que só pede produto simples
// nunca tocava em pizza/combo — agora chunk só baixa no 1º clique.
import { CartModalIsland } from './components/CartModalIsland';
import { UpsellModalIsland } from './components/UpsellModalIsland';
import { LoginIsland } from './components/LoginIsland';
import { PixModalIsland } from './components/PixModalIsland';
import { LazyOnEvent } from './components/_LazyOnEvent';
import { installAnimationGuard } from '../../shared/animationGuard';
import { instalarGuardaDoVoltar } from './utils/guardaDoVoltar';
import { SairDoAppIsland } from './components/SairDoAppIsland';
// Classificadores de produto (simples / pizza / combo) e `findProduct` vivem
// em ./utils/productKind — compartilhados com o cardápio de mesa (tablet),
// que precisa tomar EXATAMENTE a mesma decisão sobre cada item do cardápio.
import {
  findProduct,
  isSimpleProduct,
  isComboSupportedV2,
  isPizzaSupportedV2,
} from './utils/productKind';

installAnimationGuard();

const qc = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false, staleTime: 5_000 },
  },
});

function mount(el: HTMLElement, node: React.ReactNode, _label: string): Root {
  const root = createRoot(el);
  root.render(
    <StrictMode>
      {/* Sem auto-reload: o cliente pode estar no meio do checkout (endereço,
          pagamento) — recarregar sozinho apagaria o que ele digitou. */}
      <ErrorBoundary label={`delivery:${_label}`} title={t("Esta tela travou")}>
        <QueryClientProvider client={qc}>{node}</QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
  return root;
}

// ─── ILHA 1 (REMOVIDA): Product Preview Modal ─────────────────────────
// Antes V1 chamava openProductPreview() pra abrir um teaser (foto +
// descrição + botão "Comprar") e SÓ depois o wizard. Removido pra UX
// mais curta — clique no card vai DIRETO pro modal de produto.
// `openProductPreview` agora é alias de `openProductDetail` (que abaixo
// roteia simples → V2 wizard, pizza/combo → V1 wizard).
(window as unknown as { openProductPreview?: (id: number) => void }).openProductPreview = (
  productId: number,
) => {
  const w = window as Window & { openProductDetail?: (id: number) => void };
  if (typeof w.openProductDetail === 'function') {
    w.openProductDetail(productId);
  }
};

// ─── ILHA 2: Home View completa (Header + Highlights + Pills + Cards) ──
// Sinaliza ANTES de tentar montar — V1 vai pular `renderHome()` no
// próximo tick e deixar `#home-view` vazio pra React tomar conta.
const homeOwnFlag = window as unknown as {
  __V2_HOME_OWNED__?: boolean;
  __V2_HIGHLIGHTS_OWNED__?: boolean;
  __V2_STEPPERS_OWNED__?: boolean;
};
homeOwnFlag.__V2_HOME_OWNED__ = true;
// Highlights e Steppers viraram subcomponentes da Home → desativa
// as ilhas avulsas pra não duplicar.
homeOwnFlag.__V2_HIGHLIGHTS_OWNED__ = true;
homeOwnFlag.__V2_STEPPERS_OWNED__ = true;

// ─── IDIOMA ───────────────────────────────────────────────────────────
// Restaura a escolha salva (localStorage, por loja) antes do primeiro
// mount: assim a 1ª pintura já sai no idioma certo, sem piscar português.
// `window.DELIVERY_LANGS` vem do template, então isto roda depois dele.
iniciarIdioma();
// Funil do App (relatório no admin): a visita começa quando o app abre.
funilAcesso();

// ─── [REDE] ESCOLHA DA UNIDADE ────────────────────────────────────────
// Roda ANTES de montar a home: numa rede de cardápio único o cliente escolhe
// a unidade e só então vê o cardápio DELA. Ver
// `project_rede_cardapio_por_unidade.md`.
//
// Mesa/comanda passa direto: o cliente está fisicamente na loja, escolher
// unidade ali não faria sentido nenhum.
function modoEhDelivery(): boolean {
  const m = (window as unknown as { STORE_MODE?: { type?: string } }).STORE_MODE;
  const tipo = m?.type || 'delivery';
  return tipo !== 'table_service' && tipo !== 'comanda';
}

function irParaUnidade(slug: string): void {
  // Preserva a query (utm, código de indicação, etc) — o link de campanha não
  // pode morrer na troca de unidade.
  window.location.href = `/app/${encodeURIComponent(slug)}${window.location.search}`;
}

function montarEscolhaDeUnidade(unidades: NetworkUnit[], comFechar: boolean) {
  const host = document.createElement('div');
  host.id = 'delivery-v2-rede-host';
  document.body.appendChild(host);
  const root = mount(
    host,
    <NetworkUnitPickerIsland
      unidades={unidades}
      onFechar={comFechar ? () => { root.unmount(); host.remove(); } : undefined}
      onEscolher={(u) => {
        lembrarUnidade(u.slug);
        if (u.slug === window.STORE_SLUG) {
          root.unmount();
          host.remove();       // já estamos na unidade certa: só sai da frente
        } else {
          irParaUnidade(u.slug);
        }
      }}
    />,
    'rede-unidades',
  );
}

// Reabre a tela pra quem quiser trocar (botão na gaveta / barra lateral).
(window as unknown as { trocarUnidadeRede?: () => void }).trocarUnidadeRede = () => {
  const unidades = window.NETWORK_UNITS || [];
  if (unidades.length < 2) return;
  esquecerUnidade();
  montarEscolhaDeUnidade(unidades, true);
};

if (ehRedeComEscolha() && modoEhDelivery()) {
  const unidades = window.NETWORK_UNITS || [];
  const lembrada = unidadeLembrada();
  const slugAtual = String(window.STORE_SLUG || '');
  // Guarda contra loop: se o slug lembrado não levar a lugar nenhum (loja saiu
  // da rede, slug renomeado), o 2º carregamento não tenta de novo — cai na
  // tela de escolha em vez de ficar pingando entre duas URLs.
  const JA_REDIRECIONOU = 'aliv_rede_redirect';
  let jaTentou = false;
  try { jaTentou = sessionStorage.getItem(JA_REDIRECIONOU) === '1'; } catch { /* */ }

  if (lembrada && lembrada !== slugAtual && unidadePorSlug(lembrada) && !jaTentou) {
    try { sessionStorage.setItem(JA_REDIRECIONOU, '1'); } catch { /* */ }
    irParaUnidade(lembrada);
  } else {
    try { sessionStorage.removeItem(JA_REDIRECIONOU); } catch { /* */ }
    if (!lembrada) montarEscolhaDeUnidade(unidades, false);
  }
}

function mountHomeIfPresent() {
  const el = document.getElementById('home-view');
  if (!el) return;
  // Marca pra não duplicar mount no mesmo elemento.
  if ((el as HTMLElement).dataset.v2Mounted === '1') return;
  (el as HTMLElement).dataset.v2Mounted = '1';

  // Lê metadata da loja do mount point dedicado adicionado no template.
  const meta = document.getElementById('delivery-v2-meta');
  const storeName = meta?.dataset.storeName || 'Loja';
  const storeLogo = meta?.dataset.storeLogo || null;
  const bannerUrl = meta?.dataset.storeBanner || null;
  const bannerIsVideo = meta?.dataset.storeBannerIsVideo === '1';
  const isOpen = meta?.dataset.storeIsOpen === '1';
  const deliveryEstimate = meta?.dataset.storeDeliveryEstimate || '30 min';
  const minOrderValue = parseFloat(meta?.dataset.storeMinOrder || '0') || 0;

  // Limpa innerHTML (V1 pode ter populado antes da flag ser lida).
  el.innerHTML = '';
  mount(
    el,
    <HomeViewIsland
      storeName={storeName}
      storeLogo={storeLogo || null}
      bannerUrl={bannerUrl || null}
      bannerIsVideo={bannerIsVideo}
      isOpen={isOpen}
      deliveryEstimate={deliveryEstimate}
      minOrderValue={minOrderValue}
    />,
    'home-view',
  );

  // Gaveta de categorias (menu hambúrguer). Host próprio no body — ela é
  // `fixed` e por portal, então não pode nascer dentro de #home-view, que
  // o V1 ainda pode limpar.
  const gavetaHost = document.getElementById('delivery-v2-drawer-host')
    || Object.assign(document.createElement('div'), { id: 'delivery-v2-drawer-host' });
  if (!gavetaHost.isConnected) {
    document.body.appendChild(gavetaHost);
    mount(gavetaHost, <MenuDrawerIsland storeName={storeName} storeLogo={storeLogo || null} />, 'menu-drawer');
  }
}

// Tenta montar agora + re-tenta quando V1 sinaliza renderização.
mountHomeIfPresent();
window.addEventListener('aliv:home-rendered', mountHomeIfPresent);

// Banner de frete grátis — root PRÓPRIO (NÃO no header, pra não quebrar modais).
// Mount idempotente; o componente é reativo e se atualiza quando o menu carrega.
function mountFreeShippingBannerIfPresent() {
  const el = document.getElementById('free-shipping-banner-root') as (HTMLElement & { _v2Mounted?: boolean }) | null;
  if (!el || el._v2Mounted) return;
  el._v2Mounted = true;
  mount(el, <FreeShippingBanner variant="top" />, 'free-shipping-banner');
}
mountFreeShippingBannerIfPresent();
window.addEventListener('aliv:home-rendered', mountFreeShippingBannerIfPresent);

// Banner de SORTEIO ativo ("compre X e concorra a Y") — root próprio, acima
// do frete grátis. Só aparece se a loja tem campanha ativa com "exibir no app".
function mountRaffleBannerIfPresent() {
  const el = document.getElementById('raffle-banner-root') as (HTMLElement & { _v2Mounted?: boolean }) | null;
  if (!el || el._v2Mounted) return;
  el._v2Mounted = true;
  mount(el, <RaffleBanner />, 'raffle-banner');
}
mountRaffleBannerIfPresent();
window.addEventListener('aliv:home-rendered', mountRaffleBannerIfPresent);

// Esconde header V1 (CSS hook). HeaderIsland renderiza próprio header.
document.body.classList.add('v2-home-owned');

// ─── ILHA 3 (REMOVIDA): CartBarIsland ────────────────────────────────
// Barra flutuante "Ver Sacola" foi removida (atrapalhava visualização
// de produtos no fim da página). Em vez dela, BottomNavIsland destaca
// o botão Sacola com pulse + valor total quando tem itens — paridade
// UX moderna iFood (menos sobreposição).
// Flag mantida true pra V1 não renderizar a barra antiga.
(window as unknown as { __V2_CARTBAR_OWNED__?: boolean }).__V2_CARTBAR_OWNED__ = true;

// ─── ILHA 4: Bottom Nav ───────────────────────────────────────────────
const navHost = document.createElement('div');
navHost.id = 'delivery-v2-nav-host';
navHost.style.display = 'none';
document.body.appendChild(navHost);
mount(navHost, <BottomNavIsland />, 'bottom-nav');
(window as unknown as { __V2_NAV_OWNED__?: boolean }).__V2_NAV_OWNED__ = true;
// Esconde bottom nav V1.
document.body.classList.add('v2-nav-owned');

// ─── ILHA 5: Wizards (Simple/Pizza/Combo) — LAZY ──────────────────────
// Intercepta `window.openProductDetail` — roteia por tipo:
//   simples         → WizardSimpleIsland (~25 KB raw)
//   pizza suportada → WizardPizzaIsland (~65 KB raw)
//   combo suportado → WizardComboIsland (~72 KB raw)
//   demais          → V1 (legado, raro)
//
// Lazy via LazyOnEvent: chunk só baixa quando o evento dispara (1º
// clique no produto daquele tipo). Boot economiza ~40 KB gzip.
// Prefetch idle (no final do arquivo) já baixa em background quando
// browser fica ocioso → na prática, clique é instantâneo.
const wizardSimpleHost = document.createElement('div');
wizardSimpleHost.id = 'delivery-v2-wizard-simple-host';
wizardSimpleHost.style.display = 'none';
document.body.appendChild(wizardSimpleHost);
mount(
  wizardSimpleHost,
  <LazyOnEvent
    events={['aliv:open-simple-wizard', 'aliv:open-simple-wizard-edit']}
    importFn={() => import('./components/WizardSimpleIsland').then((m) => ({ default: m.WizardSimpleIsland }))}
  />,
  'wizard-simple',
);

const wizardPizzaHost = document.createElement('div');
wizardPizzaHost.id = 'delivery-v2-wizard-pizza-host';
wizardPizzaHost.style.display = 'none';
document.body.appendChild(wizardPizzaHost);
mount(
  wizardPizzaHost,
  <LazyOnEvent
    events={['aliv:open-pizza-wizard', 'aliv:open-pizza-wizard-edit']}
    importFn={() => import('./components/WizardPizzaIsland').then((m) => ({ default: m.WizardPizzaIsland }))}
  />,
  'wizard-pizza',
);

const wizardComboHost = document.createElement('div');
wizardComboHost.id = 'delivery-v2-wizard-combo-host';
wizardComboHost.style.display = 'none';
document.body.appendChild(wizardComboHost);
mount(
  wizardComboHost,
  <LazyOnEvent
    events={['aliv:open-combo-wizard', 'aliv:open-combo-wizard-edit']}
    importFn={() => import('./components/WizardComboIsland').then((m) => ({ default: m.WizardComboIsland }))}
  />,
  'wizard-combo',
);

// Salva referência V1 ANTES de sobrescrever.
const originalOpenProductDetail = (window as unknown as { openProductDetail?: (id: number) => void })
  .openProductDetail;

(window as unknown as { openProductDetail?: (id: number) => void }).openProductDetail = (
  productId: number,
) => {
  // VER/abrir o produto é SEMPRE liberado, mesmo com a loja fechada — o cliente
  // precisa conseguir navegar o cardápio. A trava de "loja fechada" passou pro
  // momento do ADD à sacola (storeOpenGuard.guardCanAddToCart, chamado nos
  // wizards e no quick-add do card). Lá ela também respeita o agendamento.
  const prod = findProduct(productId);
  // GTM/Meta: view_item (ViewContent) ao ABRIR o produto. O app nunca
  // disparava esse evento (só add_to_cart pra frente) → ViewContent não saía
  // (reclamação 10/06/2026). Dispara aqui no ponto único de abertura.
  if (prod) {
    const _vv = Number(prod.price) || 0;
    track('view_item', {
      currency: 'BRL',
      value: _vv,
      ecommerce: {
        currency: 'BRL',
        value: _vv,
        items: [{
          item_id: String(prod.id ?? productId),
          item_name: prod.name ?? '',
          price: _vv,
          quantity: 1,
        }],
      },
    });
  }
  if (isSimpleProduct(prod)) {
    window.dispatchEvent(
      new CustomEvent('aliv:open-simple-wizard', { detail: { productId } }),
    );
  } else if (prod && isPizzaSupportedV2(prod)) {
    window.dispatchEvent(
      new CustomEvent('aliv:open-pizza-wizard', { detail: { productId } }),
    );
  } else if (prod && isComboSupportedV2(prod)) {
    window.dispatchEvent(
      new CustomEvent('aliv:open-combo-wizard', { detail: { productId } }),
    );
  } else if (typeof originalOpenProductDetail === 'function') {
    originalOpenProductDetail(productId);
  } else {
    console.warn('[v2 wizard] openProductDetail V1 não disponível pra produto avançado');
  }
};

// ─── ILHA 6: Cart Modal (step 1 do checkout V1) ───────────────────────
// V2 toma só a lista da sacola; "Finalizar" delega pro V1 com step=2
// (endereço/pagamento permanece V1 até próximo batch).
const cartModalHost = document.createElement('div');
cartModalHost.id = 'delivery-v2-cart-modal-host';
cartModalHost.style.display = 'none';
document.body.appendChild(cartModalHost);
mount(cartModalHost, <CartModalIsland />, 'cart-modal');

// Salva referência V1 ANTES de sobrescrever — `handleFinalize` no Cart V2
// precisa abrir o checkout V1 pra ir pra step 2.
const originalOpenCartModal = (window as unknown as { openCartModal?: () => void })
  .openCartModal;
(window as Window & { __V1_OPEN_CART_MODAL__?: () => void }).__V1_OPEN_CART_MODAL__ =
  originalOpenCartModal;

(window as unknown as { openCartModal?: () => void }).openCartModal = () => {
  const cart = (window as Window & { CART?: import('./types').CartLine[] }).CART || [];
  if (cart.length === 0) {
    // Mantém comportamento V1 pra carrinho vazio (alert + sair).
    if (typeof originalOpenCartModal === 'function') originalOpenCartModal();
    return;
  }
  // Sinaliza pra V2 abrir a sacola.
  window.dispatchEvent(new CustomEvent('aliv:open-cart-modal'));
};

// ─── ILHA 7: Upsell Modal (entre sacola e endereço) ───────────────────
// Substitui completamente o `#upsell-modal` do V1. CartModalIsland chama
// esta ilha ao "Finalizar"; ao terminar (skip ou confirma), dispatcha
// `aliv:upsell-done` e o Cart V2 segue pro V1 endereço (step=2).
const upsellHost = document.createElement('div');
upsellHost.id = 'delivery-v2-upsell-host';
upsellHost.style.display = 'none';
document.body.appendChild(upsellHost);
mount(upsellHost, <UpsellModalIsland />, 'upsell-modal');

// Flag pro V1 saber que V2 controla o upsell — evita o V1 disparar seu
// próprio `fetchUpsellOffers()` se o usuário voltar manualmente pro
// step 1 do checkout V1 e clicar Finalizar lá (caso raro, mas possível).
(window as unknown as { __V2_UPSELL_OWNED__?: boolean }).__V2_UPSELL_OWNED__ = true;

// ─── ILHA 8: Área do Usuário (Pedidos + Endereços + Dados em tabs) ─────
// Substitui completamente `window.openUserArea(tab)` do V1. Todas as 3
// abas viraram V2: Pedidos (timeline real-time + repetir), Endereços (CRUD
// com CEP/GPS + validação bairro + geocoding distance-based), Dados
// (profile update com OTP via WhatsApp/email). Paridade EXATA com
// validações do V1 (rua/nº/bairro/cidade obrigatórios, telefone 10-13,
// email regex, senha mín 6, OTP 4 dígitos).
// LAZY: 80 KB raw — carrega só ao clicar em "Conta" no BottomNav.
const userAreaHost = document.createElement('div');
userAreaHost.id = 'delivery-v2-user-area-host';
userAreaHost.style.display = 'none';
document.body.appendChild(userAreaHost);
mount(
  userAreaHost,
  <LazyOnEvent
    events={['aliv:open-user-area']}
    importFn={() => import('./components/UserAreaIsland').then((m) => ({ default: m.UserAreaIsland }))}
  />,
  'user-area',
);

// Intercepta TODAS as chamadas — não fazemos fallback pro V1 mais, pra
// evitar o caso de uma tab abrir V1 e outra V2 e o cliente ver duas UIs
// diferentes. Se o V2 quebrar, o flag de feature toggleia a página inteira.
(window as unknown as { openUserArea?: (tab?: string) => void }).openUserArea = (
  tab?: string,
) => {
  const normalized = tab === 'addresses' || tab === 'data' || tab === 'orders' ? tab : 'orders';
  window.dispatchEvent(
    new CustomEvent('aliv:open-user-area', { detail: { tab: normalized } }),
  );
};

// V1 dispatcha `aliv:order-status-changed` sempre que o WS recebe
// atualização de status de pedido — UserAreaIsland escuta pra refetch
// em tempo real. Se V1 não disparar (versão antiga), polling 20s cobre.

// ─── ILHA 9: Login (entrar/cadastro/recover) ───────────────────────────
// Substitui `#login-modal` do V1 (delivery_app.html:1281+). Wizard de 5
// passos: identifier → password|register → recover-channel → recover-confirm.
// Intercepta `window.toggleLoginModal()` do V1 — tudo passa pelo V2 agora.
const loginHost = document.createElement('div');
loginHost.id = 'delivery-v2-login-host';
loginHost.style.display = 'none';
document.body.appendChild(loginHost);
mount(loginHost, <LoginIsland />, 'login');

// Salva referência V1 ANTES (alguns lugares chamam toggleLoginModal pra
// FECHAR algo do V1 que não está no nosso fluxo — caso raro). Hoje a V2
// só lida com abrir; fechar é responsabilidade do island (ESC/overlay/btn).
const _originalToggleLoginModal = (
  window as unknown as { toggleLoginModal?: () => void }
).toggleLoginModal;
(window as unknown as { __V1_TOGGLE_LOGIN__?: () => void }).__V1_TOGGLE_LOGIN__ =
  _originalToggleLoginModal;

(window as unknown as { toggleLoginModal?: () => void }).toggleLoginModal = () => {
  // V1 usa toggle (abre se fechado, fecha se aberto). Pra V2 só abrimos —
  // o close é via ESC/overlay/btn dentro do island. Se a modal já estiver
  // visível, o evento será no-op (LoginIsland re-monta limpo).
  window.dispatchEvent(new CustomEvent('aliv:open-login-modal'));
};

// V2 logout — substitui `window.logout()` do V1, que tinha 3 problemas:
//   1. `alert()` nativo feio "Você saiu da conta."
//   2. Não limpava `window.USER` (só o `let USER` local) → V2 (que lê de
//      window) continuava achando que cliente estava logado depois do
//      logout, dados velhos apareciam ao reabrir "Conta".
//   3. `closeUserArea()` era do V1 (não fechava nossa UserAreaIsland).
// Aqui centralizamos: limpa TUDO + chama hooks V1 que ainda servem
// (greeting da home, cart UI) + dispatcha evento pras ilhas V2 reagirem.
// O toast/confirmação fica no caller (DataTab), não aqui.
const _originalLogout = (window as unknown as { logout?: () => void }).logout;
(window as unknown as { __V1_LOGOUT__?: () => void }).__V1_LOGOUT__ = _originalLogout;

// ─── ILHA 10: Checkout (endereço + frete + pagamento, single-page) ────
// Substitui `#checkout-modal` do V1 + todo o fluxo `nextCheckoutStep()`
// → `createUnifiedOrder()`. Layout single-page (padrão iFood novo) com
// scroll natural. Anti-duplo clique em 3 camadas:
//   1. flag `submitting` na ilha (botão disabled)
//   2. body com pointer-events:none enquanto submitting
//   3. backend dedupe via Redis (idempotency_key UUID por intenção)
//
// IMPORTANTE: ESQUELETO nesta rodada — sections placeholders. Submit
// real, address/fee/payment vêm em rodadas seguintes. Cada uma vai
// passar pelo checklist de `feedback_critical_order_path.md` antes de
// subir pra prod (caminho crítico de pedido).
// LAZY: 80 KB raw — carrega só ao "Finalizar" do CartModal (após upsell).
const checkoutHost = document.createElement('div');
checkoutHost.id = 'delivery-v2-checkout-host';
checkoutHost.style.display = 'none';
document.body.appendChild(checkoutHost);
mount(
  checkoutHost,
  <LazyOnEvent
    events={['aliv:open-checkout']}
    importFn={() => import('./components/CheckoutIsland').then((m) => ({ default: m.CheckoutIsland }))}
  />,
  'checkout',
);

// ─── ILHA 11: PIX modal (QR + copy + polling WebSocket) ───────────────
// Substitui o `#pix-modal` V1. CheckoutIsland.submitPixOrder dispara
// `aliv:open-pix-modal` com { orderId, qrCode, qrCodeBase64, total }
// quando o cliente clica em "Pagar agora com PIX". WebSocket
// `payment_approved:<id>` (vindo do V1 socket.onmessage que dispatcha
// `aliv:payment-approved`) confirma o pagamento. Polling /check-payment
// a cada 5s como fallback se WS desconectar.
const pixHost = document.createElement('div');
pixHost.id = 'delivery-v2-pix-host';
pixHost.style.display = 'none';
document.body.appendChild(pixHost);
mount(pixHost, <PixModalIsland />, 'pix-modal');

// ─── ILHA 12: Order Tracking (acompanhar pedido pós-checkout) ─────────
// Modal grande com status em destaque + timeline real-time + items +
// endereço. Substitui `#order-details-modal` V1. Aberto automático
// após CheckoutIsland.success E PixModal.approved, ou manualmente
// pela UserAreaIsland.OrdersTab.
// LAZY: ~40 KB raw — carrega só após Checkout.success ou PIX approved.
const trackingHost = document.createElement('div');
trackingHost.id = 'delivery-v2-tracking-host';
trackingHost.style.display = 'none';
document.body.appendChild(trackingHost);
mount(
  trackingHost,
  <LazyOnEvent
    events={['aliv:open-order-tracking']}
    importFn={() => import('./components/OrderTrackingIsland').then((m) => ({ default: m.OrderTrackingIsland }))}
  />,
  'order-tracking',
);

// ─── ILHA 13: Store Info (modal "Sobre a loja") ───────────────────────
// Substitui `openStoreInfo()` V1 (SweetAlert grande com horários,
// endereço, contatos, mapa, redes sociais). Mesmo conteúdo em V2 com
// idioma iFood (sheet branco, sem libs). Aberta via `openStoreInfo()`
// interceptado abaixo — BottomNav Loja continua chamando essa função.
// LAZY: ~15 KB raw — botão "Loja" raríssimo de clicar (<0.5% sessions).
const storeInfoHost = document.createElement('div');
storeInfoHost.id = 'delivery-v2-storeinfo-host';
storeInfoHost.style.display = 'none';
document.body.appendChild(storeInfoHost);
mount(
  storeInfoHost,
  <LazyOnEvent
    events={['aliv:open-store-info']}
    importFn={() => import('./components/StoreInfoIsland').then((m) => ({ default: m.StoreInfoIsland }))}
  />,
  'store-info',
);

// ─── ILHA 14: aviso de cookies DISCRETO (LGPD) ───────────────────────
// O banner grande saiu em 12/10/2026 (cobria a base da tela); em 28/10 o dono
// pediu um aviso discreto de volta, pra Auditoria LGPD voltar a ter registro.
// Faixa de uma linha, uma vez por aparelho (components/CookieAvisoIsland).
const cookieHost = document.createElement('div');
cookieHost.id = 'delivery-v2-cookie-host';
cookieHost.style.display = 'none';
document.body.appendChild(cookieHost);
mount(cookieHost, <CookieAvisoIsland />, 'cookie-aviso');

// ─── ILHA 15: PWA install banner (Android beforeinstallprompt + iOS) ─
// Substitui `#pwa-install-banner` V1 (delivery_app.html:2191) + funções
// installPWA/dismissInstall/detectIosBrowser (linha 12740-12912).
// Android/PC: beforeinstallprompt → prompt nativo. iOS Safari real:
// modal explicando Share → Adicionar à Tela de Início. iOS Chrome/IG/
// WhatsApp/etc: modal pedindo pra abrir no Safari (limitação da Apple).
const pwaMeta = document.getElementById('delivery-v2-meta');
const pwaLogo = pwaMeta?.dataset.storeLogo || '';
const pwaHost = document.createElement('div');
pwaHost.id = 'delivery-v2-pwa-host';
pwaHost.style.display = 'none';
document.body.appendChild(pwaHost);
mount(pwaHost, <PwaInstallBannerIsland logoUrl={pwaLogo} />, 'pwa-install');

// ─── ILHA 16: Push notifications (modal pós-login + subscribe VAPID) ─
// Expõe window.initPushNotifications / askForNotificationPermission /
// subscribeUserToPush. LoginIsland já chama window.initPushNotifications
// após login bem-sucedido — esse handler tem origem aqui agora (não
// mais Swal V1).
const pushHost = document.createElement('div');
pushHost.id = 'delivery-v2-push-host';
pushHost.style.display = 'none';
document.body.appendChild(pushHost);
mount(pushHost, <PushNotificationsIsland />, 'push-notifications');

// ─── ILHA 17: Global Search (lupa do header) ──────────────────────────
// Substitui openGlobalSearch() V1 (SweetAlert + re-render do menu).
// Modal full-screen com input + filtro em tempo real + click → wizard.
const searchHost = document.createElement('div');
searchHost.id = 'delivery-v2-search-host';
searchHost.style.display = 'none';
document.body.appendChild(searchHost);
mount(searchHost, <GlobalSearchIsland />, 'global-search');

(window as unknown as { openGlobalSearch?: () => void }).openGlobalSearch = () => {
  window.dispatchEvent(new CustomEvent('aliv:open-global-search'));
};

// ─── ILHA 18: Bairro Picker (modal lista de bairros com fee) ──────────
// LAZY: aberto via aliv:open-bairro-picker pelo UserAreaIsland.
const bairroHost = document.createElement('div');
bairroHost.id = 'delivery-v2-bairro-host';
bairroHost.style.display = 'none';
document.body.appendChild(bairroHost);
mount(
  bairroHost,
  <LazyOnEvent
    events={['aliv:open-bairro-picker']}
    importFn={() =>
      import('./components/BairroPickerIsland').then((m) => ({
        default: m.BairroPickerIsland,
      }))
    }
  />,
  'bairro-picker',
);

// ─── ILHA 19: Loyalty Modal (programa de fidelidade) ──────────────────
// LAZY: aberta via aliv:open-loyalty-modal (UserAreaIsland mostra link
// "Meus pontos" se loja tem programa de fidelidade ativo).
const loyaltyHost = document.createElement('div');
loyaltyHost.id = 'delivery-v2-loyalty-host';
loyaltyHost.style.display = 'none';
document.body.appendChild(loyaltyHost);
mount(
  loyaltyHost,
  <LazyOnEvent
    events={['aliv:open-loyalty-modal']}
    importFn={() =>
      import('./components/LoyaltyModalIsland').then((m) => ({
        default: m.LoyaltyModalIsland,
      }))
    }
  />,
  'loyalty-modal',
);

(window as unknown as { openLoyaltyModal?: () => void }).openLoyaltyModal = () => {
  window.dispatchEvent(new CustomEvent('aliv:open-loyalty-modal'));
};

// ─── ILHA 20: Table/Comanda Mode (banner sticky + check-table + prompt) ──
// Sempre montada (decide visibilidade lendo window.STORE_MODE no boot).
// Pra modo delivery padrão, retorna null. Pra mesa/comanda, mostra
// banner sticky + lógica de check-table / prompt de comanda.
const tableHost = document.createElement('div');
tableHost.id = 'delivery-v2-table-host';
tableHost.style.display = 'none';
document.body.appendChild(tableHost);
mount(tableHost, <TableModeBannerIsland />, 'table-mode-banner');

// ─── ILHA 21: Botão VOLTAR do celular ─────────────────────────────────
// App de página única: sem isto o voltar do Android sai do app com o
// combo/sacola/checkout aberto. Voltar fecha o modal do topo (ou recua a
// etapa do wizard); sem modal, pergunta "Sair da loja?". Ver
// utils/guardaDoVoltar.ts.
const sairHost = document.createElement('div');
sairHost.id = 'delivery-v2-sair-host';
document.body.appendChild(sairHost);
mount(sairHost, <SairDoAppIsland />, 'sair-do-app');
instalarGuardaDoVoltar();

const _originalOpenStoreInfo = (window as unknown as { openStoreInfo?: () => void })
  .openStoreInfo;
(window as unknown as { __V1_OPEN_STORE_INFO__?: () => void }).__V1_OPEN_STORE_INFO__ =
  _originalOpenStoreInfo;
(window as unknown as { openStoreInfo?: () => void }).openStoreInfo = () => {
  window.dispatchEvent(new CustomEvent('aliv:open-store-info'));
};

// Salva referência V1 ANTES — caso algo dê errado no V2 ou um caller
// muito antigo precise do fallback (paridade UpsellModal flag).
const _originalNextCheckoutStep = (
  window as unknown as { nextCheckoutStep?: () => void }
).nextCheckoutStep;
(window as unknown as { __V1_NEXT_CHECKOUT_STEP__?: () => void }).__V1_NEXT_CHECKOUT_STEP__ =
  _originalNextCheckoutStep;

// V1 chama `nextCheckoutStep()` em vários lugares (UpsellModal V1 done,
// botão Finalizar do checkout V1, atalhos de tecla). V2 intercepta TUDO
// e abre a CheckoutIsland — não tem fallback pro V1 pra evitar UX híbrido
// onde o cliente vê dois modais.
(window as unknown as { nextCheckoutStep?: () => void }).nextCheckoutStep = () => {
  window.dispatchEvent(new CustomEvent('aliv:open-checkout'));
};

// CartModalIsland V2 dispatcha `aliv:upsell-done` quando o cliente passa
// pelo upsell — V1 antes ouvia esse evento pra abrir o checkout V1. Agora
// abrimos o V2 direto.
window.addEventListener('aliv:upsell-done', () => {
  window.dispatchEvent(new CustomEvent('aliv:open-checkout'));
});

// Flag pro V1 saber que V2 controla o checkout — paridade com
// `__V2_UPSELL_OWNED__`. Se algum atalho V1 escapar, ele pode checar e
// não duplicar a UI.
(window as unknown as { __V2_CHECKOUT_OWNED__?: boolean }).__V2_CHECKOUT_OWNED__ = true;

// Defesa em profundidade: força o `#checkout-modal` V1 (sidebar 480px
// do desktop) a ficar SEMPRE oculto. V1 tem múltiplos caminhos que
// fazem `classList.remove('hidden')` — qualquer um esquecido deixava a
// sidebar visível atrás do CheckoutIsland V2 e o cliente via ela depois
// que o V2 fechava ("carrinho V1 esquecido no canto direito").
//
// CSS via !important garante que mesmo se alguém remover a class .hidden
// programaticamente, o display:none vence. Funciona como kill switch
// definitivo até a gente conseguir deletar o `<div id="checkout-modal">`
// do template (rodada do `delivery_pure.html`).
try {
  const v1KillCss = document.createElement('style');
  v1KillCss.id = 'v2-kill-v1-modals';
  // #checkout-modal (sidebar 480px do desktop) — substituído por CheckoutIsland
  // #pix-modal (V1 z-[99990] alto) — substituído por PixModalIsland
  // #order-details-modal (tracking pós-pedido) — virá em OrderTrackingIsland
  v1KillCss.textContent =
    '#checkout-modal,#pix-modal,#order-details-modal{display:none!important;}';
  document.head.appendChild(v1KillCss);
} catch {
  /* */
}

(window as unknown as { logout?: () => void }).logout = () => {
  try {
    localStorage.removeItem('wabiz_user');
    localStorage.removeItem('wabiz_token');
  } catch {
    /* quota / privacy */
  }
  try {
    (window as unknown as { USER?: unknown }).USER = null;
  } catch {
    /* */
  }
  // Hooks V1 que ainda controlam a home (greeting) e o badge do carrinho.
  // Não chamamos o `_originalLogout` inteiro pq ele dispara `alert()` e
  // mexe em `closeUserArea` V1 — já cobrimos tudo aqui de forma limpa.
  try {
    (window as unknown as { updateGreeting?: () => void }).updateGreeting?.();
  } catch {
    /* */
  }
  try {
    (window as unknown as { updateCartUI?: () => void }).updateCartUI?.();
  } catch {
    /* */
  }
  window.dispatchEvent(new CustomEvent('aliv:logout'));
};

// ─── Intercepta editar item no carrinho (V1 `editCartItem(idx)`) ──────
// Item simples → abre WizardSimpleIsland pré-preenchido. Pizza/combo →
// delega pro V1 (próximos batches farão wizard de pizza/combo em React).
// Importante: NÃO removemos o item do CART aqui (V1 fazia splice antes
// de abrir o modal); a ilha substitui o item no `addToCart` via splice
// na posição `editIndex`. Se o usuário fechar sem salvar, o item fica
// intacto — UX que o V1 não tinha (V1 sumia se você fechasse o wizard).
import type { CartLine } from './types';

const originalEditCartItem = (window as unknown as { editCartItem?: (idx: number) => void })
  .editCartItem;

(window as unknown as { editCartItem?: (idx: number) => void }).editCartItem = (
  index: number,
) => {
  const cart = (window as Window & { CART?: CartLine[] }).CART || [];
  const line = cart[index];
  if (!line) {
    console.warn('[v2 edit] índice inválido no CART:', index);
    return;
  }
  const prod = findProduct(line.product_id as number);
  const cartItemType = (line as CartLine & { type?: string }).type;

  // Tipo do CART line é fonte de verdade primária:
  //   - 'product' → simples (rota V2 se possível)
  //   - 'pizza'   → pizza (rota V2 se suportada)
  //   - 'combo'   → V1
  const looksSimpleByType = !cartItemType || cartItemType === 'product';
  if (looksSimpleByType && isSimpleProduct(prod)) {
    window.dispatchEvent(
      new CustomEvent('aliv:open-simple-wizard-edit', {
        detail: { productId: line.product_id, cartIndex: index, line },
      }),
    );
    return;
  }
  if (cartItemType === 'pizza' && prod && isPizzaSupportedV2(prod)) {
    window.dispatchEvent(
      new CustomEvent('aliv:open-pizza-wizard-edit', {
        detail: { productId: line.product_id, cartIndex: index, line },
      }),
    );
    return;
  }
  if (cartItemType === 'combo' && prod && isComboSupportedV2(prod)) {
    window.dispatchEvent(
      new CustomEvent('aliv:open-combo-wizard-edit', {
        detail: { productId: line.product_id, cartIndex: index, line },
      }),
    );
    return;
  }
  if (typeof originalEditCartItem === 'function') {
    originalEditCartItem(index);
  } else {
    console.warn('[v2 edit] editCartItem V1 não disponível pra item avançado');
  }
};

// ─── Prefetch de chunks lazy via requestIdleCallback ─────────────────
// Boot termina → browser ocioso → pré-baixa em background os chunks
// dos modais lazy (Wizards, Checkout, UserArea, OrderTracking, etc).
// Quando o cliente clica, o chunk já está em cache → modal abre
// instantâneo (sem download + parse no momento do click).
//
// Vantagens: zero impacto no TTI inicial (idleCallback respeita CPU),
// melhora UX percebida pra modais/wizards lazy. Browsers sem rIC
// caem no setTimeout(2s) — também ocioso o suficiente pro boot terminar.
//
// import() dinâmico do Vite resolve igual ao LazyOnEvent, então o
// cache fica unificado (não duplica fetch).
function _prefetchLazyChunks() {
  const prefetch = () => {
    // Ordem aproximada de uso (mais comum primeiro). Tipo `() => Promise<unknown>`
    // pra unificar — cada import resolve pra módulo diferente, TS não consegue
    // inferir union sem hint.
    const imports: Array<() => Promise<unknown>> = [
      () => import('./components/WizardSimpleIsland'),
      () => import('./components/CheckoutIsland'),
      () => import('./components/WizardPizzaIsland'),
      () => import('./components/UserAreaIsland'),
      () => import('./components/WizardComboIsland'),
      () => import('./components/OrderTrackingIsland'),
      () => import('./components/StoreInfoIsland'),
      () => import('./components/BairroPickerIsland'),
      () => import('./components/LoyaltyModalIsland'),
    ];
    // Encadeia em série + 100ms gap pra não saturar a banda do cliente.
    let p: Promise<unknown> = Promise.resolve();
    for (const fn of imports) {
      p = p
        .then(() => fn())
        .then(() => new Promise((r) => setTimeout(r, 100)))
        .catch(() => { /* silencioso — chunk vai ser baixado no event */ });
    }
  };
  type IdleCallbackWindow = Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number;
  };
  const w = window as IdleCallbackWindow;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(prefetch, { timeout: 5000 });
  } else {
    setTimeout(prefetch, 2000);
  }
}
_prefetchLazyChunks();
